/** /reseller-policies — compile a tenant's reseller suppression matrix.
 *
 * Ops maintains `do_not_send_{concern}_{channel}` by hand on reseller tag data.
 * This turns that matrix into feature-flag and blacklist rows that enforce it.
 *
 * The page is built around one rule: **preview is free, compiling is not.**
 * Picking an operator previews; compiling is a separate, explicit button behind
 * a confirmation, because it changes which messages real guests receive. There
 * is no compile-everything, here or in the API.
 *
 * The report's second half is given the same weight as the first. Declared
 * policy the platform cannot enforce, unrecognised keys and values that are not
 * really booleans are all shown — ops believes that policy is in force, and a
 * page that quietly dropped it would be worse than one that never offered it.
 */
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import * as policiesApi from '@/api/resellerPolicies';
import type { PolicyOutcome, PolicyOutcomeStatus, PolicyReport } from '@/api/resellerPolicies';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { PageHeader } from '@/components/PageHeader';
import { useSnackbar } from '@/components/Snackbar';
import { TenantPicker } from '@/components/TenantPicker/TenantPicker';
import { operatorActions } from '@/components/ValueActions/identity';
import { ValueActions } from '@/components/ValueActions/ValueActions';
import { usePermissions } from '@/hooks/usePermissions';
import { PAGE_FILL_SX } from '@/layout/pageFill';
import { useScopedTenantState } from '@/scope/useScopedTenantState';

import { policyOutcomeActions, policyTagActions } from './featureFlagLinks';

const STATUS_COLOR: Record<PolicyOutcomeStatus, 'success' | 'info' | 'warning' | 'error'> = {
  created: 'success',
  updated: 'success',
  forced: 'warning',
  unchanged: 'info',
  diverged: 'error',
  skipped: 'warning',
};

/** Statuses that mean the row would change — what "pending" counts. */
const WRITING: PolicyOutcomeStatus[] = ['created', 'updated', 'forced'];

function OutcomeRow({
  outcome,
  showTags = true,
  tenantId,
}: {
  outcome: PolicyOutcome;
  showTags?: boolean;
  tenantId?: number | null;
}) {
  const rowActions = policyOutcomeActions(outcome);
  return (
    <Box sx={{ py: 0.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
        <Chip size="small" color={STATUS_COLOR[outcome.status]} label={outcome.status} />
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: 12 }}>
          {outcome.label}
        </Typography>
        {/* The row the compiler wrote. A feature flag opens in its grid; a
            blacklist rule has no deep link, so its id only copies. */}
        {rowActions.length > 0 && (
          <Typography variant="caption" color="text.secondary">
            <ValueActions actions={rowActions}>#{outcome.row_id}</ValueActions>
          </Typography>
        )}
        {outcome.reason && (
          <Typography variant="caption" color="text.secondary">
            — {outcome.reason}
          </Typography>
        )}
      </Box>
      {showTags && outcome.tags.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ pl: 1 }} component="div">
          {outcome.tags.length} reseller{outcome.tags.length === 1 ? '' : 's'}:{' '}
          {outcome.tags.map((t, i) => (
            <Box component="span" key={t.id}>
              {i > 0 && ', '}
              <ValueActions actions={policyTagActions(t, tenantId)}>{t.name}</ValueActions>
            </Box>
          ))}
        </Typography>
      )}
    </Box>
  );
}

/** The same outcomes keyed by reseller tag rather than by compiled row.
 *
 * "What does this Viator tag enforce?" is the question ops asks; the row label
 * ("feature app_download / every channel @ acme") answers the other direction.
 * Both views are the same data — only the key differs — so this is a toggle, not
 * a second fetch.
 */
function ByTagView({ outcomes, tenantId }: { outcomes: PolicyOutcome[]; tenantId?: number | null }) {
  const byTag = new Map<number, { name: string; outcomes: PolicyOutcome[] }>();
  for (const o of outcomes) {
    for (const t of o.tags) {
      const entry = byTag.get(t.id) ?? { name: t.name, outcomes: [] };
      entry.outcomes.push(o);
      byTag.set(t.id, entry);
    }
  }
  const rows = [...byTag.entries()].sort((a, b) => a[1].name.localeCompare(b[1].name));

  if (rows.length === 0) {
    return (
      <Alert severity="info">
        No reseller tag carries policy that compiles to anything. Rows without tags — the
        unenforceable ones below — never reach this view.
      </Alert>
    );
  }
  return (
    <Stack spacing={1.5}>
      {rows.map(([id, entry]) => (
        <Box key={id}>
          <Typography variant="subtitle2">
            <ValueActions actions={policyTagActions({ id, name: entry.name }, tenantId)}>
              {entry.name}
            </ValueActions>
          </Typography>
          {entry.outcomes.map((o, i) => (
            <OutcomeRow key={`${o.label}-${i}`} outcome={o} showTags={false} tenantId={tenantId} />
          ))}
        </Box>
      ))}
    </Stack>
  );
}

function ReportView({
  report,
  dryRun,
  tenantId,
}: {
  report: PolicyReport;
  dryRun: boolean;
  tenantId?: number | null;
}) {
  const diverged = report.outcomes.filter((o) => o.status === 'diverged');
  const [groupBy, setGroupBy] = useState<'row' | 'tag'>('row');
  return (
    <Stack spacing={2}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Read {report.tags_read} reseller tag{report.tags_read === 1 ? '' : 's'}.
          {dryRun ? ' Nothing was written.' : ''}
        </Typography>
        {report.outcomes.length > 0 && (
          <ToggleButtonGroup
            size="small"
            exclusive
            value={groupBy}
            onChange={(_e, v) => v && setGroupBy(v as 'row' | 'tag')}
            aria-label="Group compiled rows by"
          >
            <ToggleButton value="row">By rule</ToggleButton>
            <ToggleButton value="tag">By reseller</ToggleButton>
          </ToggleButtonGroup>
        )}
      </Box>

      {report.outcomes.length === 0 ? (
        <Alert severity="info">
          No policy to compile. Ops writes this matrix as <code>do_not_send_…</code> attributes on
          the operator&apos;s reseller tags, or on a canonical those tags inherit from.
        </Alert>
      ) : groupBy === 'tag' ? (
        <ByTagView outcomes={report.outcomes} tenantId={tenantId} />
      ) : (
        <Box>
          {report.outcomes.map((o, i) => (
            <OutcomeRow key={`${o.label}-${i}`} outcome={o} tenantId={tenantId} />
          ))}
        </Box>
      )}

      {diverged.length > 0 && (
        <Alert severity="error">
          <AlertTitle>
            {diverged.length} row{diverged.length === 1 ? '' : 's'} edited since the compiler last
            wrote {diverged.length === 1 ? 'it' : 'them'}
          </AlertTitle>
          Those edits are kept, not overwritten. Overwriting a named concern is a{' '}
          <code>manage.py reseller_policy_compile --force</code> run — deliberately not a button,
          because &ldquo;discard what a human changed&rdquo; should not be one click away.
        </Alert>
      )}

      {report.unenforceable.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>Declared, but not enforceable</AlertTitle>
          This policy exists in the tag data and the platform cannot act on it. It is listed rather
          than dropped, because somebody believes it is in force.
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
            {report.unenforceable.map((n) => (
              <li key={n}>{n}</li>
            ))}
          </Box>
        </Alert>
      )}

      {report.unknown_keys.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>Unrecognised suppression keys</AlertTitle>
          Not part of the standard grammar, so nothing reads them.
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
            {report.unknown_keys.map((k) => (
              <li key={k}>
                <code>{k}</code>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {report.ambiguous_values.length > 0 && (
        <Alert severity="warning">
          <AlertTitle>Values that are not really booleans</AlertTitle>
          Read with the same truthiness the templates already apply — so <code>
            &quot;0&quot;
          </code>{' '}
          and <code>&quot;false&quot;</code> are non-empty strings and therefore{' '}
          <strong>true</strong>. Matching the templates is deliberate; fixing the value on the tag
          is the remedy.
          <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }}>
            {report.ambiguous_values.map((v) => (
              <li key={v}>
                <code>{v}</code>
              </li>
            ))}
          </Box>
        </Alert>
      )}
    </Stack>
  );
}

export function ResellerPoliciesPage() {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_notification_features_manage');
  const snackbar = useSnackbar();
  const qc = useQueryClient();

  const [scope, setScope] = useScopedTenantState();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [written, setWritten] = useState<PolicyReport | null>(null);

  const previewQuery = useQuery({
    queryKey: ['reseller-policies', 'preview', scope?.id],
    queryFn: () => policiesApi.preview(scope!.id),
    enabled: scope !== null,
  });

  const driftQuery = useQuery({
    queryKey: ['reseller-policies', 'drift'],
    queryFn: () => policiesApi.drift(),
  });

  const compileMutation = useMutation({
    mutationFn: () => policiesApi.compile(scope!.id),
    onSuccess: (report) => {
      setWritten(report);
      const n = report.outcomes.filter((o) => WRITING.includes(o.status)).length;
      snackbar.show(`Compiled ${report.tour_operator}: ${n} row(s) written.`, 'success');
      void qc.invalidateQueries({ queryKey: ['reseller-policies'] });
      void qc.invalidateQueries({ queryKey: ['notification-feature-flags'] });
    },
    onError: (e) =>
      snackbar.show(e instanceof ConsoleApiError ? e.message : 'Compile failed.', 'error'),
  });

  const report = written ?? previewQuery.data ?? null;
  const pending = report ? report.outcomes.filter((o) => WRITING.includes(o.status)).length : 0;

  return (
    <Box sx={PAGE_FILL_SX}>
      <PageHeader
        helpStory="reseller-policies"
        title="Reseller Policies"
        subtitle="Compile the do_not_send matrix ops maintains on reseller tags into the feature flags and blacklist rules that enforce it."
      />

      <Stack spacing={3} sx={{ maxWidth: 1000, pb: 4 }}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <Box sx={{ minWidth: 320 }}>
              <TenantPicker
                value={scope}
                onChange={(t) => {
                  setScope(t);
                  setWritten(null);
                }}
                label="Operator"
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<PlayArrowIcon />}
              disabled={!canManage || scope === null || compileMutation.isPending || pending === 0}
              onClick={() => setConfirmOpen(true)}
            >
              {compileMutation.isPending ? 'Compiling…' : 'Compile'}
            </Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Picking an operator previews what a compile would do and writes nothing. One operator at
            a time, by design — compiling changes which messages their guests receive.
          </Typography>
        </Paper>

        {scope === null && (
          <Alert severity="info" icon={<CompareArrowsIcon />}>
            Pick an operator to see what compiling would change.
          </Alert>
        )}

        {scope !== null && previewQuery.isPending && !written && <CircularProgress size={24} />}
        {scope !== null && previewQuery.isError && !written && (
          <Alert severity="error">Could not read this operator&apos;s policy.</Alert>
        )}

        {report && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 1.5 }}>
              {written ? 'Compiled' : 'Preview'} ·{' '}
              {scope ? (
                <ValueActions
                  actions={operatorActions({ id: scope.id, code: report.tour_operator })}
                >
                  {report.tour_operator}
                </ValueActions>
              ) : (
                report.tour_operator
              )}
            </Typography>
            <ReportView report={report} dryRun={written === null} tenantId={scope?.id ?? null} />
          </Paper>
        )}

        <Divider />

        <Box>
          <Typography variant="h6" sx={{ mb: 0.5 }}>
            Drift
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Operators that have already compiled and whose rows no longer match their tag data.
            Operators that never compiled are not listed — they have not opted in, and listing
            everyone who ever wrote a reseller tag is how a real warning gets ignored.
          </Typography>
          {driftQuery.isPending && <CircularProgress size={20} />}
          {driftQuery.data?.count === 0 && (
            <Alert severity="success">Every compiled operator is in step with its tag data.</Alert>
          )}
          {driftQuery.data?.results.map((row) => (
            <Paper key={row.tour_operator_id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
              {/* The button owns the click (it previews the operator here);
                  hover adds the operator's own page. */}
              <ValueActions
                chip
                actions={operatorActions({ id: row.tour_operator_id, code: row.tour_operator })}
              >
                <Link
                  component="button"
                  type="button"
                  onClick={() => {
                    setWritten(null);
                    setScope({ id: row.tour_operator_id, name: row.tour_operator });
                  }}
                  sx={{ fontWeight: 600 }}
                >
                  {row.tour_operator}
                </Link>
              </ValueActions>
              <Typography variant="body2" color="text.secondary">
                {row.pending.length} row{row.pending.length === 1 ? '' : 's'} would change
              </Typography>
            </Paper>
          ))}
        </Box>
      </Stack>

      <ConfirmDialog
        open={confirmOpen}
        title={`Compile reseller policy for ${scope?.code ?? scope?.name ?? ''}?`}
        message={
          `This writes ${pending} row${pending === 1 ? '' : 's'} that change which messages ` +
          `this operator's guests receive. Rows a human has edited since the compiler last ` +
          `wrote them are reported, not overwritten.`
        }
        confirmLabel="Compile"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          setConfirmOpen(false);
          compileMutation.mutate();
        }}
      />
    </Box>
  );
}
